import { useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from './useLandingReveal';

const MODEL_BASE = `${process.env.PUBLIC_URL || ''}/models`;

const CAMERA_Z = 6;
const CAMERA_FOV = 45;
// Icon size as a share of the visible frame at the z = 0 plane: 13.5% of its
// height, but never more than 22% of its width — without the width clamp the
// icons balloon on tall, narrow viewports.
const BASE_SIZE = 0.135;
const MAX_WIDTH_SHARE = 0.22;
const NARROW_BREAKPOINT = 900;
const MAX_FRAME_DELTA = 0.05;

const HALF_PI = Math.PI / 2;

/**
 * One floating icon per app. Placement is expressed in fractions of the visible
 * frame at each icon's own depth, so the icons hold their spot on screen at any
 * viewport size while `z` still buys real perspective (further ones read
 * smaller and drift less). `preRotate` faces a model's artwork at the camera —
 * the source GLBs are not authored on a common axis.
 */
const ICON_LAYOUT = [
  { model: 'instagram', x: -0.40, y: 0.16, z: -1.0, narrowX: -0.30, narrowY: 0.27, scale: 1.00, tilt: [0.38, -0.50, 0.16], spin: 0.13, bob: 0.26, speed: 0.42, phase: 0.0, narrow: true },
  { model: 'youtube', x: 0.40, y: 0.24, z: -2.6, narrowX: 0.27, narrowY: 0.27, scale: 0.92, tilt: [-0.28, 0.55, -0.20], spin: -0.10, bob: 0.30, speed: 0.35, phase: 1.9, narrow: true },
  { model: 'meta', x: -0.37, y: -0.22, z: -4.0, scale: 0.85, tilt: [0.20, 0.45, 0.28], spin: 0.08, bob: 0.34, speed: 0.30, phase: 3.4, narrow: false, preRotate: [HALF_PI, 0, 0] },
  { model: 'tiktok', x: 0.38, y: -0.18, z: -1.8, scale: 0.72, tilt: [-0.34, -0.35, -0.12], spin: -0.15, bob: 0.22, speed: 0.47, phase: 0.9, narrow: false },
  { model: 'facebook', x: -0.31, y: 0.30, z: -6.0, scale: 0.78, tilt: [0.16, 0.80, -0.24], spin: 0.06, bob: 0.40, speed: 0.26, phase: 2.6, narrow: false },
  { model: 'google', x: 0.30, y: 0.32, z: -5.0, scale: 0.66, tilt: [-0.18, -0.70, 0.20], spin: 0.11, bob: 0.36, speed: 0.32, phase: 4.7, narrow: false },
];

/**
 * Floating 3D app-icon cubes behind the hero headline. Purely decorative: the
 * layer is inert to pointer events and bails out quietly whenever WebGL, the
 * models, or the browser is not cooperating.
 */
function HeroFloatingIcons() {
  const containerRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let disposed = false;
    let teardown = () => {};

    (async () => {
      let THREE;
      let GLTFLoader;
      let MeshoptDecoder;
      let RoomEnvironment;
      try {
        [THREE, { GLTFLoader }, { MeshoptDecoder }, { RoomEnvironment }] = await Promise.all([
          import('three'),
          import('three/examples/jsm/loaders/GLTFLoader.js'),
          import('three/examples/jsm/libs/meshopt_decoder.module.js'),
          import('three/examples/jsm/environments/RoomEnvironment.js'),
        ]);
      } catch (_) {
        return; // three failed to load; hero stays flat.
      }
      if (disposed) return;

      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({
          alpha: true,
          antialias: true,
          powerPreference: 'low-power',
        });
      } catch (_) {
        return; // No WebGL context available.
      }

      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;
      renderer.domElement.setAttribute('aria-hidden', 'true');

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.1, 100);
      camera.position.set(0, 0, CAMERA_Z);

      // Half the icons are metal/roughness PBR and need an environment to read
      // as anything but black; the rest are emissive and ignore lighting.
      const pmrem = new THREE.PMREMGenerator(renderer);
      const environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      scene.environment = environment;
      scene.add(new THREE.AmbientLight(0xffffff, 0.6));
      const keyLight = new THREE.DirectionalLight(0xffffff, 1.6);
      keyLight.position.set(2, 3, 4);
      scene.add(keyLight);

      const group = new THREE.Group();
      scene.add(group);

      const icons = [];
      const pointer = { x: 0, y: 0 };
      const parallax = { x: 0, y: 0 };
      const clock = new THREE.Clock();
      let elapsed = 0;
      let frameId = null;
      let running = false;
      let width = 0;
      let height = 0;
      let resizeObserver = null;
      let intersectionObserver = null;

      const frameHeightAt = (z) =>
        2 * Math.tan((CAMERA_FOV * Math.PI) / 360) * (CAMERA_Z - z);

      const layout = () => {
        if (!icons.length || !height) return;
        const aspect = width / height;
        const frameH0 = frameHeightAt(0);
        const iconSize = Math.min(BASE_SIZE * frameH0, MAX_WIDTH_SHARE * frameH0 * aspect);
        const narrow = width < NARROW_BREAKPOINT;

        icons.forEach((icon) => {
          const { config, object } = icon;
          object.visible = !narrow || config.narrow;
          if (!object.visible) return;

          const frameH = frameHeightAt(config.z);
          const frameW = frameH * aspect;
          // Narrow frames have no side gutters, so the icons that survive move
          // up into the band above the headline instead.
          icon.baseX = (narrow ? config.narrowX : config.x) * frameW;
          icon.baseY = (narrow ? config.narrowY : config.y) * frameH;
          object.position.set(icon.baseX, icon.baseY, config.z);
          object.scale.setScalar(config.scale * iconSize * icon.unit);
        });
      };

      const resize = () => {
        const rect = container.getBoundingClientRect();
        width = Math.max(1, Math.round(rect.width));
        height = Math.max(1, Math.round(rect.height));
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false);
        layout();
        // Resizing clears the drawing buffer, so repaint when the loop is idle.
        if (!running) renderFrame();
      };

      const renderFrame = () => {
        parallax.x += (pointer.x * 0.35 - parallax.x) * 0.04;
        parallax.y += (pointer.y * 0.22 - parallax.y) * 0.04;

        icons.forEach((icon) => {
          const { config, object } = icon;
          if (!object.visible) return;
          // Nearer icons ride the pointer harder — cheap depth cue.
          const depth = 1 + config.z / 8;
          object.position.x = icon.baseX + parallax.x * depth;
          object.position.y =
            icon.baseY +
            Math.sin(elapsed * config.speed + config.phase) * config.bob -
            parallax.y * depth;
          object.rotation.y = config.tilt[1] + elapsed * config.spin;
          object.rotation.x =
            config.tilt[0] +
            Math.sin(elapsed * config.speed * 0.7 + config.phase) * 0.12;
        });

        renderer.render(scene, camera);
      };

      const tick = () => {
        if (disposed) return;
        // Clamped so a paused tab or a stalled frame never jumps the loop.
        elapsed += Math.min(clock.getDelta(), MAX_FRAME_DELTA);
        renderFrame();
        frameId = requestAnimationFrame(tick);
      };

      const stop = () => {
        running = false;
        if (frameId !== null) cancelAnimationFrame(frameId);
        frameId = null;
      };

      const start = () => {
        if (running || disposed || reducedMotion) return;
        running = true;
        clock.getDelta(); // Drop the time spent paused.
        frameId = requestAnimationFrame(tick);
      };

      const handlePointerMove = (event) => {
        pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
        pointer.y = (event.clientY / window.innerHeight) * 2 - 1;
      };

      const dispose = () => {
        stop();
        if (resizeObserver) resizeObserver.disconnect();
        if (intersectionObserver) intersectionObserver.disconnect();
        window.removeEventListener('pointermove', handlePointerMove);
        scene.traverse((child) => {
          if (!child.isMesh) return;
          child.geometry?.dispose();
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((material) => {
            if (!material) return;
            Object.values(material).forEach((value) => {
              if (value && value.isTexture) value.dispose();
            });
            material.dispose();
          });
        });
        environment.dispose();
        pmrem.dispose();
        renderer.dispose();
        renderer.domElement.remove();
      };

      teardown = dispose;

      // Don't spend a phone's bandwidth on models it will never show. A later
      // resize past the breakpoint keeps the narrow set rather than fetching.
      const wanted = container.getBoundingClientRect().width < NARROW_BREAKPOINT
        ? ICON_LAYOUT.filter((config) => config.narrow)
        : ICON_LAYOUT;

      const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
      const loaded = await Promise.all(
        wanted.map((config) =>
          loader
            .loadAsync(`${MODEL_BASE}/${config.model}.glb`)
            .then((gltf) => ({ config, gltf }))
            .catch(() => null)
        )
      );
      if (disposed) {
        dispose();
        return;
      }

      loaded.forEach((entry) => {
        if (!entry) return; // This model failed to load; the rest still fly.
        const { config, gltf } = entry;
        const model = gltf.scene;
        if (config.preRotate) model.rotation.set(...config.preRotate);

        // Normalize each model to a unit cube around its own centroid — the
        // source GLBs differ in both scale and pivot.
        const bounds = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        bounds.getSize(size);
        bounds.getCenter(center);
        const unit = 1 / Math.max(size.x, size.y, size.z || 1);

        const pivot = new THREE.Group();
        pivot.add(model);
        // Offset inside the pivot so rotation happens about the icon's middle.
        model.position.sub(center);

        const object = new THREE.Group();
        object.add(pivot);
        object.rotation.set(config.tilt[0], config.tilt[1], config.tilt[2]);
        group.add(object);
        icons.push({ config, object, unit, baseX: 0, baseY: 0 });
      });

      if (!icons.length) {
        dispose();
        return;
      }

      container.appendChild(renderer.domElement);
      resize();

      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(container);

      // Only burn frames while the hero is on screen. A backgrounded tab is
      // already handled for us — browsers park requestAnimationFrame there.
      intersectionObserver = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? start() : stop()),
        { threshold: 0 }
      );
      intersectionObserver.observe(container);

      if (!reducedMotion) {
        window.addEventListener('pointermove', handlePointerMove, { passive: true });
      }

      renderFrame(); // First paint before the loop (and the only one if reduced).
      container.classList.add('lp-hero__scene--ready');
    })();

    return () => {
      disposed = true;
      teardown();
    };
  }, [reducedMotion]);

  return <div ref={containerRef} className="lp-hero__scene" aria-hidden="true" />;
}

export default HeroFloatingIcons;
